import React from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import {
  disableVerticalSwipes,
  enableClosingConfirmation,
  init,
  initData,
  mountBackButton,
  mountClosingBehavior,
  mountSettingsButton,
  mountSwipeBehavior,
  mountViewport,
  postEvent,
  requestFullscreen,
  retrieveLaunchParams,
} from "@telegram-apps/sdk";
import { PostHogProvider } from "posthog-js/react";
import TelegramAnalytics from "@telegram-apps/analytics";

// Import CSS files
import "./global.css";

// Important: load mock environment before any other app code
import "./mockEnv";

// Initialize i18n system
import "./utils/i18n";

import isDesktop from "./utils/isDesktop";
import { initLanguage } from "./utils/i18n";
import { applyPlatformSafeArea } from "./utils/getPlatformSafeArea";
import { App } from "./App";

// Extend Window interface to include hideLoadingScreen
declare global {
  interface Window {
    hideLoadingScreen?: () => void;
  }
}

// Use Vite's built-in DEV flag
const IS_DEV = import.meta.env.DEV;

// Initialize Telegram Mini App
const initializeTelegramApp = async (platform?: string) => {
  try {
    // Try to mount Telegram-specific behaviors
    try {
      mountBackButton.ifAvailable?.();
      mountSettingsButton.ifAvailable?.();
      mountSwipeBehavior.ifAvailable?.();
      disableVerticalSwipes.ifAvailable?.();
      mountClosingBehavior.ifAvailable?.();
      enableClosingConfirmation.ifAvailable?.();
    } catch (e) {
      // Failed to mount some Telegram behaviors
    }

    // Try to lock orientation
    try {
      postEvent("web_app_toggle_orientation_lock", { locked: true });
    } catch (e) {
      // Failed to lock orientation
    }

    if (!isDesktop(platform)) {
      // Try to set up mobile-specific features
      if (mountViewport.isAvailable()) {
        try {
          await mountViewport();
        } catch (e) {
          // Failed to mount viewport
        }
      }

      if (requestFullscreen.isAvailable()) {
        try {
          await requestFullscreen();
        } catch (e) {
          // Failed to request fullscreen
        }
      }
    }
  } catch (e) {
    console.error("Initialize Telegram App failed:", e);
    throw e;
  }
};

// Global error handler to catch TelegramAnalytics errors
window.addEventListener("error", (event) => {
  const errorMessage = event.error?.message || event.message || "";
  // Suppress TelegramAnalytics errors related to window.Telegram
  if (
    errorMessage.includes("window.Telegram") ||
    errorMessage.includes("@telegram-apps/analytics")
  ) {
    console.warn("Suppressed TelegramAnalytics error:", errorMessage);
    event.preventDefault();
    return false;
  }
});

// Global unhandled promise rejection handler for TelegramAnalytics
window.addEventListener("unhandledrejection", (event) => {
  const reason = event.reason?.message || event.reason || "";
  if (
    typeof reason === "string" &&
    (reason.includes("window.Telegram") ||
      reason.includes("@telegram-apps/analytics"))
  ) {
    console.warn("Suppressed TelegramAnalytics promise rejection:", reason);
    event.preventDefault();
    return false;
  }
});

// Initialize app
window.addEventListener("load", async () => {
  let platform: string | undefined;

  if (!IS_DEV) {
    try {
      init();
    } catch (e) {
      console.error("Failed to initialize Telegram SDK:", e);
    }

    try {
      TelegramAnalytics.init({
        token:
          "eyJhcHBfbmFtZSI6ImV2aWxfY29pbl9nYW1lIiwiYXBwX3VybCI6Imh0dHBzOi8vdC5tZS9ldmlsX2NvaW5fYm90IiwiYXBwX2RvbWFpbiI6Imh0dHBzOi8vZXZpbGNvaW4uZ2V0Ym9vbmQuY29tLyJ9!HfMmz+IeQD1yswNH8nGZpPzBWrzx20weTiuRHVkmv2U=",
        appName: "evil_coin_game",
      });
    } catch (e) {
      console.error("Failed to initialize Telegram Analytics:", e);
      // Continue without analytics - app should still work
    }
  }

  initData.restore();

  try {
    // In production, we need to be more careful with errors
    try {
      const { tgWebAppPlatform: platformFromParams, tgWebAppData } =
        retrieveLaunchParams();
      platform = platformFromParams;
      await initializeTelegramApp(platform);
    } catch (error) {
      // For production, we'll still try to initialize with unknown platform
      await initializeTelegramApp(undefined);
    }
  } catch (error) {
    // Last resort fallback - at this point we'll just continue without Telegram functionality
  }

  // Initialize language
  await initLanguage();

  // Apply platform-specific safe area adjustments
  applyPlatformSafeArea();

  // Ensure we always start with /menu route on first open, regardless of initial URL
  const currentPath = window.location.pathname;
  if (currentPath !== "/menu") {
    window.history.replaceState(null, "", "/menu");
  }

  // Mount React app
  const container = document.getElementById("root");
  if (!container) {
    throw new Error("Root container not found");
  }

  const root = createRoot(container);
  root.render(
    <PostHogProvider
      apiKey={import.meta.env.VITE_PUBLIC_POSTHOG_KEY}
      options={{
        api_host: import.meta.env.VITE_PUBLIC_POSTHOG_HOST,
        defaults: "2025-05-24",
        capture_exceptions: true,
        debug: import.meta.env.MODE === "development",
      }}
    >
      <BrowserRouter>
        <App platform={platform} />
      </BrowserRouter>
    </PostHogProvider>
  );

  // Note: Loading screen hiding is now handled by AppWithLoadingState component
  // which waits for i18n resources to load before hiding the loading screen
});
